import { categoryLabel } from "./PlaceCard";

/**
 * Bohème chic typographic placeholder — used when we don't have a verified
 * photo of an establishment. Honest by design: a wrong photo would be worse
 * than no photo. Composition: vintage warm cream paper, hand-drawn monogram,
 * editorial display serif, decorative flourish, micro-etched grain.
 */
export default function PhotoPlaceholder({ name, category, size = "default" }) {
    const isLarge = size === "large";
    const px = isLarge ? "px-10 py-14 md:px-16 md:py-20" : "px-6 py-9";
    const titleCls = isLarge
        ? "text-4xl md:text-6xl lg:text-7xl"
        : "text-2xl md:text-3xl";

    return (
        <div
            data-testid="photo-placeholder"
            className={`absolute inset-0 flex flex-col justify-between ${px} text-cp-encre overflow-hidden`}
            style={{
                // Layered cream paper — warm cp-ivoire with diagonal grain + soft vignette
                background:
                    "radial-gradient(ellipse at 30% 20%, rgba(214,191,160,0.22) 0%, rgba(214,191,160,0) 55%)," +
                    "radial-gradient(ellipse at 70% 90%, rgba(60,80,68,0.10) 0%, rgba(60,80,68,0) 50%)," +
                    "linear-gradient(135deg, #f4ecd8 0%, #ebe1c8 50%, #e6d8b8 100%)",
            }}
        >
            {/* fine etching layer */}
            <div
                aria-hidden
                className="absolute inset-0 pointer-events-none"
                style={{
                    backgroundImage:
                        "repeating-linear-gradient(135deg, rgba(23,20,16,0.04) 0 1px, transparent 1px 9px)",
                    mixBlendMode: "multiply",
                }}
            />
            {/* corner ornaments */}
            <div aria-hidden className="absolute top-3 left-3 w-8 h-8 border-l border-t border-cp-foret/40" />
            <div aria-hidden className="absolute top-3 right-3 w-8 h-8 border-r border-t border-cp-foret/40" />
            <div aria-hidden className="absolute bottom-3 left-3 w-8 h-8 border-l border-b border-cp-foret/40" />
            <div aria-hidden className="absolute bottom-3 right-3 w-8 h-8 border-r border-b border-cp-foret/40" />

            {/* HEADER */}
            <div className="relative flex items-center justify-between">
                <span className="cp-tiny text-cp-foret tracking-[0.22em]">
                    {categoryLabel(category) || "Adresse"}
                </span>
                <span className="cp-tiny text-cp-sable-deep tracking-[0.18em] opacity-70">
                    Côte Perdue
                </span>
            </div>

            {/* CENTRE — monogram + name */}
            <div className="relative flex flex-col items-center text-center gap-3 md:gap-5">
                {/* monogram CP */}
                <svg
                    width={isLarge ? "76" : "44"}
                    height={isLarge ? "76" : "44"}
                    viewBox="0 0 80 80"
                    fill="none"
                    className="text-cp-foret/70"
                    aria-hidden
                >
                    <circle cx="40" cy="40" r="38" stroke="currentColor" strokeWidth="0.6" />
                    <text
                        x="50%"
                        y="55%"
                        dominantBaseline="middle"
                        textAnchor="middle"
                        fontFamily="'Cormorant Garamond', 'Playfair Display', serif"
                        fontSize="34"
                        fontStyle="italic"
                        fill="currentColor"
                    >
                        cp
                    </text>
                </svg>

                <h4 className={`cp-serif italic leading-[0.95] text-cp-encre ${titleCls} max-w-[12ch]`}>
                    {name}
                </h4>

                {isLarge && (
                    <div className="w-16 h-px bg-cp-foret/40 mt-2" />
                )}
            </div>

            {/* FOOTER — honesty mention */}
            <div className="relative flex items-end justify-between gap-4">
                <p className="cp-tiny italic text-cp-gris leading-relaxed max-w-[28ch]">
                    Photo officielle à venir — nous attendons l'autorisation de l'établissement.
                </p>
                <span className="cp-tiny tracking-[0.22em] text-cp-foret/70">№ {(name || "").length.toString().padStart(2, "0")}</span>
            </div>
        </div>
    );
}
